export interface RetryOptions {
  /** Total number of attempts, including the first one. */
  attempts: number;
  /**
   * Decides whether a failed attempt may be retried. Called once for every failure, in order, including the
   * failure of the last attempt, so it can also run per-failure side effects. Defaults to retrying every error.
   */
  shouldRetry?: (error: unknown, attempt: number) => boolean;
  /** Delay in milliseconds before the attempt that follows the given failed attempt. Defaults to no delay. */
  delayMs?: (failedAttempt: number) => number;
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Calls `fn` until it resolves, up to `attempts` times. Attempts never overlap: the next one starts only after the
 * previous one failed (and after its delay). Rethrows the error of the last attempt, or the first error that
 * `shouldRetry` rejects. Each attempt is chained from the previous failure, so there is no await inside a loop.
 */
export const retry = <T>(fn: (attempt: number) => Promise<T>, options: RetryOptions): Promise<T> => {
  const { attempts, shouldRetry = () => true, delayMs } = options;
  if (!Number.isInteger(attempts) || attempts < 1) {
    return Promise.reject(new RangeError("attempts must be a positive integer"));
  }
  const runAttempt = async (attempt: number): Promise<T> => {
    try {
      return await fn(attempt);
    } catch (error) {
      if (!shouldRetry(error, attempt) || attempt >= attempts) {
        throw error;
      }
      const delay = delayMs?.(attempt) ?? 0;
      if (delay > 0) {
        await wait(delay);
      }
      return runAttempt(attempt + 1);
    }
  };
  return runAttempt(1);
};
